
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Trash2, AlertCircle } from "lucide-react";

interface CriterionType {
  name: string;
  description: string;
  max_points: number;
}

interface EnhancedCriterionCardProps {
  criterion: CriterionType;
  index: number;
  canDelete: boolean;
  onUpdate: (index: number, field: string, value: string | number) => void;
  onDelete: (index: number) => void;
}

export const EnhancedCriterionCard = ({
  criterion,
  index,
  canDelete,
  onUpdate,
  onDelete,
}: EnhancedCriterionCardProps) => {
  const hasValidation = !criterion.name.trim() || !criterion.description.trim();

  return (
    <Card className={`transition-colors ${hasValidation ? 'border-red-200 dark:border-red-800' : 'border-border'}`}>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">Criterion {index + 1}</Label>
            {hasValidation && (
              <AlertCircle className="h-4 w-4 text-red-500" />
            )}
          </div>
          {canDelete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(index)}
              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 space-y-2">
            <Label htmlFor={`criterion-name-${index}`} className={`text-sm ${!criterion.name.trim() ? 'text-red-600 dark:text-red-400' : ''}`}>
              Name {!criterion.name.trim() && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id={`criterion-name-${index}`}
              value={criterion.name}
              onChange={(e) => onUpdate(index, 'name', e.target.value)}
              placeholder="e.g., Technical Accuracy"
              className={!criterion.name.trim() ? 'border-red-300 dark:border-red-700 focus:border-red-500' : ''}
            />
            {!criterion.name.trim() && (
              <p className="text-xs text-red-600 dark:text-red-400">Please enter a criterion name</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor={`criterion-points-${index}`} className="text-sm">Points</Label>
            <Input
              id={`criterion-points-${index}`}
              type="number"
              min="1"
              max="10"
              value={criterion.max_points}
              onChange={(e) => onUpdate(index, 'max_points', parseInt(e.target.value) || 1)}
              className="text-center"
            />
            <p className="text-xs text-muted-foreground text-center">1-10 points</p>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`criterion-description-${index}`} className={`text-sm ${!criterion.description.trim() ? 'text-red-600 dark:text-red-400' : ''}`}>
            Description {!criterion.description.trim() && <span className="text-red-500">*</span>}
          </Label>
          <Textarea
            id={`criterion-description-${index}`}
            value={criterion.description}
            onChange={(e) => onUpdate(index, 'description', e.target.value)}
            placeholder="Describe what this criterion evaluates and how to score it. Include specific examples of what constitutes different score levels."
            rows={3}
            className={!criterion.description.trim() ? 'border-red-300 dark:border-red-700 focus:border-red-500' : ''}
          />
          {!criterion.description.trim() && (
            <p className="text-xs text-red-600 dark:text-red-400">Please provide a clear description for evaluators</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
